import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { RegionSelector } from "@/components/RegionSelector";
import { useSmoothScroll } from "@/hooks/use-smooth-scroll";

const navLinks = [
  { label: "Properties", href: "/properties", isPage: true },
  { label: "Insights", href: "/blog", isPage: true },
  { label: "Destinations", href: "#destinations", isPage: false },
  { label: "Partners", href: "/partners", isPage: true },
  { label: "Team", href: "/team", isPage: true },
  { label: "Contact", href: "#contact", isPage: false },
];

// Premium animation variants
const navItemVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: [0.6, 0.01, 0.05, 0.95] as [number, number, number, number],
    },
  }),
};

const underlineVariants = {
  initial: { scaleX: 0, originX: 0 },
  hover: { 
    scaleX: 1, 
    transition: { 
      duration: 0.3, 
      ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
    } 
  },
};

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { handleAnchorClick } = useSmoothScroll({ offset: 80 });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, link: typeof navLinks[0]) => {
    if (link.isPage) {
      setIsMobileMenuOpen(false);
      return;
    }
    handleAnchorClick(e, link.href, () => setIsMobileMenuOpen(false));
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-background/95 backdrop-blur-md shadow-subtle py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between">
            <Link to="/">
              <Logo size="md" variant={isScrolled ? "dark" : "light"} />
            </Link>

            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link, index) =>
                link.isPage ? (
                  <motion.div
                    key={link.label}
                    custom={index}
                    variants={navItemVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                    className="relative"
                  >
                    <Link
                      to={link.href}
                      className={`font-body text-sm uppercase tracking-[0.15em] transition-colors duration-300 block py-1 ${
                        isScrolled
                          ? "text-muted-foreground hover:text-foreground"
                          : "text-cream/80 hover:text-cream"
                      }`}
                    >
                      {link.label}
                    </Link>
                    <motion.span
                      variants={underlineVariants}
                      className="absolute -bottom-0.5 left-0 right-0 h-px bg-gold"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key={link.label}
                    custom={index}
                    variants={navItemVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                    className="relative"
                  >
                    <a
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link)}
                      className={`font-body text-sm uppercase tracking-[0.15em] transition-colors duration-300 block py-1 ${
                        isScrolled
                          ? "text-muted-foreground hover:text-foreground"
                          : "text-cream/80 hover:text-cream"
                      }`}
                    >
                      {link.label}
                    </a>
                    <motion.span
                      variants={underlineVariants}
                      className="absolute -bottom-0.5 left-0 right-0 h-px bg-gold"
                    />
                  </motion.div>
                )
              )}
            </div>

            {/* Region Selector & CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <RegionSelector />
              <Button variant="luxury" size="lg" asChild>
                <a href="#contact" onClick={(e) => handleNavClick(e, { label: "Contact", href: "#contact", isPage: false })}>
                  Private Inquiry
                </a>
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 ${isScrolled ? "text-foreground" : "text-cream"}`}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-background pt-24 lg:hidden"
          >
            <div className="container mx-auto px-6">
              <div className="flex flex-col gap-6">
                {navLinks.map((link, index) =>
                  link.isPage ? (
                    <motion.div
                      key={link.label}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="font-display text-3xl text-foreground hover:text-gold transition-colors block"
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ) : (
                    <motion.a
                      key={link.label}
                      href={link.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={(e) => handleNavClick(e, link)}
                      className="font-display text-3xl text-foreground hover:text-gold transition-colors"
                    >
                      {link.label}
                    </motion.a>
                  )
                )}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="pt-6"
                >
                  <Button variant="luxury" size="xl" className="w-full" asChild>
                    <a href="#contact" onClick={(e) => handleNavClick(e, { label: "Contact", href: "#contact", isPage: false })}>
                      Private Inquiry
                    </a>
                  </Button>
                </motion.div>
                
                {/* Mobile Region Selector */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <RegionSelector variant="mobile" />
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
